import { useCallback, useEffect } from "react";
import { Path, PathValue, UseFormReturn } from "react-hook-form";

export const useCopyFormFields = <R extends Record<string, unknown>>(
  fieldsToCopy: [from: Path<R>, to: Path<R>][],
  form: UseFormReturn<R>,
  shouldCopy?: boolean,
) => {
  const copyValues = useCallback(() => {
    fieldsToCopy.forEach(([from, to]) => {
      const sourceValue = form.getValues(from);
      const targetValue = form.getValues(to);
      if (sourceValue !== targetValue) {
        form.setValue(to, sourceValue as PathValue<R, Path<R>>);
        form.clearErrors(to);
      }
    });
  }, [fieldsToCopy, form]);

  useEffect(() => {
    if (!shouldCopy) return;

    const subscription = form.watch((_, { name }) => {
      // Only copy if the changed field is a source field
      if (fieldsToCopy.some(([from]) => from === name)) {
        copyValues();
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [copyValues, fieldsToCopy, form, shouldCopy]);

  return copyValues;
};
